import {useMemo, useState} from "react";
import {App as AntdApp, Alert, Segmented, Tabs, Modal, Input} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetGscOverviewQuery,
	useGetGscQueriesQuery,
	useGetGscPagesQuery,
	useGetGscCountriesQuery,
	useGetGscSearchTypesQuery,
	useDisconnectGscMutation,
} from "~/reduxs/api/gscApiSlice";
import GscTrendChart from "./GscTrendChart";
import GscTopTable from "./GscTopTable";
import GscContent from "./GscContent";
import GscCountries from "./GscCountries";
import GscSearchTypes from "./GscSearchTypes";
import GscSitemaps from "./GscSitemaps";
import GscAnalysis from "./GscAnalysis";
import GscUrlInspect from "./GscUrlInspect";
import GscCannibalization from "./GscCannibalization";
import GscPropertyPicker from "./GscPropertyPicker";
import {TrendBadge, bucketRows, BUCKETS} from "./GscTrend";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

/** Trừ (n-1) ngày từ chuỗi Y-m-d → chuỗi Y-m-d (mốc bắt đầu cho preset khoảng). */
function minusDays(ymd, n) {
	const d = new Date(`${ymd}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() - (n - 1));
	return d.toISOString().slice(0, 10);
}

const RANGES = [
	{label: '7 ngày', value: 7},
	{label: '28 ngày', value: 28},
	{label: '3 tháng', value: 90},
	{label: '16 tháng', value: 480},
];

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

/**
 * Báo cáo GSC phục vụ từ DB, phân mục theo tab cho dễ xem:
 *  - Tổng quan: 4 thẻ (clicks/impressions/CTR/vị trí) kèm % so kỳ trước + biểu đồ xu hướng.
 *  - Từ khoá / Trang: bảng top kèm cột "Thay đổi" + nhóm Đầu bảng/Đang tăng/Đang giảm.
 *  - Quốc gia: thanh bar theo tỉ trọng clicks.
 *  - Loại tìm kiếm: lưu lượng hình ảnh/video/tin tức.
 * Có chọn khoảng ngày (preset), banner tiến độ backfill/độ trễ, đổi property, ngắt kết nối.
 */
function GscReports({projectId, connection}) {

	const {notification} = AntdApp.useApp();
	const [days, setDays] = useState(28);
	const [changing, setChanging] = useState(false);
	const [queryBucket, setQueryBucket] = useState('top');
	const [pageBucket, setPageBucket] = useState('top');

	// Tab 2 tầng: cụm (Hiệu suất/Phân tích/Công cụ) + tab con nhớ theo từng cụm.
	const [group, setGroup] = useState('perf');
	const [activeTabs, setActiveTabs] = useState({perf: 'overview', analysis: 'analysis', tools: 'inspect'});

	// Ngắt kết nối: xoá 16 tháng dữ liệu backfill, không undo → bắt gõ lại property.
	const [disconnectOpen, setDisconnectOpen] = useState(false);
	const [confirmText, setConfirmText] = useState('');

	const freshest = connection?.freshest_date;

	// Khoảng ngày tính từ ngày mới nhất GSC có dữ liệu (freshest), không phải hôm nay.
	const {from, to} = useMemo(() => {
		if (!freshest) return {from: undefined, to: undefined};
		return {from: minusDays(freshest, days), to: freshest};
	}, [freshest, days]);

	const {data: overview, isFetching: loadingOverview} = useGetGscOverviewQuery({projectId, from, to});
	const {data: queries = [], isFetching: loadingQueries} = useGetGscQueriesQuery({projectId, from, to, limit: 100});
	const {data: pages = [], isFetching: loadingPages} = useGetGscPagesQuery({projectId, from, to, limit: 100});
	const {data: countries = [], isFetching: loadingCountries} = useGetGscCountriesQuery({projectId, from, to, limit: 50});
	const {data: searchTypes = [], isFetching: loadingTypes} = useGetGscSearchTypesQuery({projectId, from, to});

	const [disconnect, {isLoading: disconnecting}] = useDisconnectGscMutation();

	const confirmMatched = confirmText.trim() === connection.property_url;

	const handleDisconnect = async () => {
		if (!confirmMatched) return;
		try {
			await disconnect(projectId).unwrap();
			notification.success({message: 'Đã ngắt kết nối'});
			setDisconnectOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không ngắt được kết nối')});
		}
	};

	const queryRows = useMemo(() => bucketRows(queries, queryBucket), [queries, queryBucket]);
	const pageRows  = useMemo(() => bucketRows(pages, pageBucket), [pages, pageBucket]);

	if (changing) {
		return <GscPropertyPicker projectId={projectId} email={connection?.email} onCancel={() => setChanging(false)} />;
	}

	const summary = overview?.summary || {clicks: 0, impressions: 0, ctr: 0, position: 0};
	const delta   = overview?.delta || {};

	const cards = [
		{label: 'Clicks', color: '#4f46e5', value: fmt(summary.clicks), delta: delta.clicks},
		{label: 'Impressions', color: '#0ea5e9', value: fmt(summary.impressions), delta: delta.impressions},
		{label: 'CTR trung bình', color: '#16a34a', value: `${(summary.ctr * 100).toFixed(1)}%`, delta: delta.ctr},
		{label: 'Vị trí trung bình', color: '#d97706', value: Number(summary.position || 0).toFixed(1), delta: delta.position},
	];

	// Nội dung tab "Tổng quan".
	const overviewTab = (
		<>
			<div className={cn('cards')}>
				{cards.map((c) => (
					<div key={c.label} className={cn('card')}>
						<div className={cn('cardLabel')}><span className={cn('dot')} style={{background: c.color}} /> {c.label}</div>
						<div className={cn('cardValue')}>{c.value}</div>
						<div className={cn('cardDelta')}><TrendBadge value={c.delta} /> <span className={cn('cardDeltaHint')}>so kỳ trước</span></div>
					</div>
				))}
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Xu hướng theo ngày</h3>
				<GscTrendChart series={overview?.series || []} />
			</div>
		</>
	);

	// Bảng top có nhóm Đầu bảng/Tăng/Giảm (dùng chung cho Từ khoá & Trang).
	const dimTab = (rows, loading, bucket, setBucket, isPage, label) => (
		<>
			<div className={cn('rangeBar')} style={{marginBottom: 12}}>
				<Segmented options={BUCKETS} value={bucket} onChange={setBucket} size="small" />
				<span style={{color: '#9ca3af', fontSize: 12}}>{rows.length} mục</span>
			</div>
			<GscTopTable rows={rows} loading={loading} isPage={isPage} label={label} showTrend />
		</>
	);

	// 10 tab gom thành 3 cụm để 1 hàng tab không quá dài.
	const TAB_GROUPS = {
		perf: [
			{key: 'overview', label: 'Tổng quan', children: overviewTab},
			{key: 'content', label: 'Nội dung', children: <GscContent projectId={projectId} from={from} to={to} />},
			{key: 'queries', label: 'Từ khoá', children: dimTab(queryRows, loadingQueries || loadingOverview, queryBucket, setQueryBucket, false, 'Từ khoá')},
			{key: 'pages', label: 'Trang', children: dimTab(pageRows, loadingPages, pageBucket, setPageBucket, true, 'Trang')},
			{key: 'countries', label: 'Quốc gia', children: <GscCountries rows={countries} loading={loadingCountries} />},
			{key: 'searchtypes', label: 'Loại tìm kiếm', children: <GscSearchTypes rows={searchTypes} loading={loadingTypes} />},
		],
		analysis: [
			{
				key: 'analysis', label: 'Phân tích', children: (
					<>
						<Alert type="info" showIcon style={{marginBottom: 14}}
							message="Khác tab Từ khoá/Trang (số liệu thô): tab này chắt lọc sẵn insight — quick wins và trang/từ khoá đang tăng-giảm mạnh." />
						<GscAnalysis projectId={projectId} from={from} to={to} />
					</>
				),
			},
			{key: 'cannibal', label: 'Cannibalization', children: <GscCannibalization projectId={projectId} />},
		],
		tools: [
			{key: 'inspect', label: 'Kiểm tra URL', children: <GscUrlInspect projectId={projectId} propertyUrl={connection.property_url} />},
			{key: 'sitemaps', label: 'Sitemap', children: <GscSitemaps projectId={projectId} />},
		],
	};

	const GROUP_OPTIONS = [
		{value: 'perf', label: <span><FontAwesomeIcon icon="fa-light fa-chart-line" /> Hiệu suất</span>},
		{value: 'analysis', label: <span><FontAwesomeIcon icon="fa-light fa-lightbulb" /> Phân tích</span>},
		{value: 'tools', label: <span><FontAwesomeIcon icon="fa-light fa-toolbox" /> Công cụ</span>},
	];

	return (
		<div>
			{/* Thanh thông tin kết nối + hành động */}
			<div className={cn('section')} style={{paddingTop: 14, paddingBottom: 14}}>
				<div className={cn('rangeBar')} style={{marginBottom: 0}}>
					<div>
						<div style={{fontWeight: 600}}>{connection.property_url}</div>
						<div className={cn('metaRow')}>
							{connection.email && <span><FontAwesomeIcon icon="fa-brands fa-google" /> {connection.email}</span>}
							{connection.last_synced_date && <span>Dữ liệu tới ngày {connection.last_synced_date}</span>}
						</div>
					</div>
					<div style={{display: 'flex', gap: 8}}>
						<Button white small onClick={() => setChanging(true)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-arrows-rotate" />}>Đổi property</Button>
						<Button red small onClick={() => { setConfirmText(''); setDisconnectOpen(true); }}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-link-slash" />}>Ngắt kết nối</Button>
					</div>
				</div>
			</div>

			{connection.backfill_done === false && (
				<Alert type="info" showIcon style={{marginBottom: 16}}
					message="Đang đồng bộ dữ liệu lịch sử (tối đa 16 tháng)"
					description="Số liệu sẽ đầy dần trong vài giờ. Bạn có thể xem trước phần dữ liệu đã có." />
			)}

			<Alert type="warning" showIcon banner style={{marginBottom: 16}}
				message={`Lưu ý: GSC trễ 2–3 ngày và có lấy mẫu. Dữ liệu mới nhất tới ${freshest || '—'}.`} />

			{/* Chọn khoảng ngày (áp dụng cho mọi tab) */}
			<div className={cn('rangeBar')}>
				<Segmented options={RANGES} value={days} onChange={setDays} />
				<span style={{color: '#9ca3af', fontSize: 12}}>{from} → {to}</span>
			</div>

			{/* Phân mục báo cáo: cụm (Segmented) → tab con (nhớ tab theo từng cụm) */}
			<div className={cn('section')}>
				<Segmented
					options={GROUP_OPTIONS}
					value={group}
					onChange={setGroup}
					style={{marginBottom: 4}}
				/>
				<Tabs
					items={TAB_GROUPS[group]}
					activeKey={activeTabs[group]}
					onChange={(key) => setActiveTabs((t) => ({...t, [group]: key}))}
				/>
			</div>

			{/* Xác nhận ngắt kết nối: mất 16 tháng dữ liệu backfill, không undo → bắt gõ lại property */}
			<Modal
				open={disconnectOpen}
				title="Ngắt kết nối Google Search Console"
				okText="Ngắt kết nối"
				okButtonProps={{danger: true, disabled: !confirmMatched}}
				confirmLoading={disconnecting}
				cancelText="Huỷ"
				onOk={handleDisconnect}
				onCancel={() => setDisconnectOpen(false)}
			>
				<Alert type="error" showIcon style={{marginBottom: 14}}
					message="Hành động không thể hoàn tác"
					description="Toàn bộ số liệu đã đồng bộ (backfill tối đa 16 tháng) của dự án này sẽ bị xoá. Kết nối lại sẽ phải đồng bộ từ đầu." />
				<p style={{marginBottom: 8}}>
					Gõ lại chính xác property <code style={{background: '#f3f4f6', padding: '1px 6px', borderRadius: 4}}>{connection.property_url}</code> để xác nhận:
				</p>
				<Input
					value={confirmText}
					onChange={(e) => setConfirmText(e.target.value)}
					placeholder={connection.property_url}
					onPressEnter={handleDisconnect}
					status={confirmText && !confirmMatched ? 'error' : undefined}
				/>
			</Modal>
		</div>
	);
}

export default GscReports;
